import type { Issue } from './types';

/**
 * BULK CARD CHANGES — the pure logic behind the floating bulk bar
 * (project-bulk-card-changes). Nothing here touches the store, the backend or
 * React, so every rule below is unit-tested on its own: vendor confinement, the
 * one-digest-per-recipient shape, and the draft a duplicate is built from.
 */

/* ── Vendor confinement — who a selection can be assigned to (requirement #3) ──
 *
 * A person is offered to a SELECTION only if they belong to EVERY board the
 * selection spans. On one board that is simply "are they a member of this board";
 * across boards (the Grid / My Work home) it is "are they on all of them". A
 * vendor confined to one board is never silently dropped — the picker SHOWS them
 * locked, with how far short they fall, so the limit is legible, not invisible
 * (R15/R28). The board NAMES and the fix wording are the view's to format; this
 * function supplies the counts the lock line is built from.
 */
export interface Assignability {
  /** How many of the selected cards sit on a board this person belongs to. */
  onCount: number;
  /** The selected cards in all. */
  total: number;
  /** Assignable to the WHOLE selection — a member of every selected card's board. */
  assignable: boolean;
}

export function assignability(
  cardBoards: string[],
  memberBoards: ReadonlySet<string>,
): Assignability {
  const total = cardBoards.length;
  const onCount = cardBoards.reduce((n, b) => (memberBoards.has(b) ? n + 1 : n), 0);
  return { onCount, total, assignable: total > 0 && onCount === total };
}

/* ── Notification batching — one digest per recipient (requirement #5) ────────
 *
 * A bulk change must NEVER fire N separate call-outs. The recipients and the
 * cards that concern each of them are collapsed to ONE digest row per person —
 * one bell item ("assigned you 7 cards" / "moved 7 of your cards") and, through
 * the existing scheduled email, one email listing the cards (which reaches a
 * seatless vendor by their notify address). The actor is never notified about
 * their own action, and a card with no recipient raises nothing.
 *
 * This is the SHAPE the store hands the backend; writing the rows themselves is a
 * SECURITY DEFINER job (a browser cannot write a call-out FOR another person —
 * notifications have no client insert policy, 0105/0106), so the store passes
 * these digests to `backend.bulkNotify`, which calls the matching RPC. The pure
 * grouping is proven here so "one row per recipient" is testable without a DB.
 */
export type BulkDigestKind = 'assigned_bulk' | 'moved_bulk';

export interface BulkDigestItem {
  recipient: string;
  ref: string;
  title: string;
  boardId: string;
}

export interface BulkDigest {
  owner: string;
  kind: BulkDigestKind;
  boardId: string;
  refs: string[];
  titles: string[];
  count: number;
}

export function bulkDigests(
  actor: string,
  kind: BulkDigestKind,
  items: BulkDigestItem[],
): BulkDigest[] {
  const me = (actor || '').trim().toLowerCase();
  const byOwner = new Map<string, BulkDigest>();
  for (const it of items) {
    const owner = (it.recipient || '').trim().toLowerCase();
    if (!owner) continue; // a card nobody owns calls out nobody
    if (owner === me) continue; // never notify yourself about your own action
    let d = byOwner.get(owner);
    if (!d) {
      d = { owner, kind, boardId: it.boardId, refs: [], titles: [], count: 0 };
      byOwner.set(owner, d);
    }
    d.refs.push(it.ref);
    d.titles.push(it.title);
    d.count += 1;
  }
  return [...byOwner.values()];
}

/* ── Duplicate — the draft a bulk Duplicate makes (requirement, the bar) ──────
 *
 * A duplicate copies the WORK, not its past: the new card carries the same title,
 * scope, owner, dates and classification, but starts with a clean slate — no
 * running narration, not marked finished, not blocked, not on hold, and fresh
 * comments (createIssue mints the id/ref/timestamps/comments). Built by stripping
 * the minted fields and resetting the lifecycle marks, so TypeScript guarantees
 * every remaining required field is still present.
 */
export function duplicateDraft(
  issue: Issue,
): Omit<Issue, 'id' | 'ref' | 'createdAt' | 'updatedAt' | 'comments'> {
  // Pull off the fields createIssue mints itself; keep the rest as the copy's base.
  const { id: _id, ref: _ref, createdAt: _c, updatedAt: _u, comments: _cm, ...rest } = issue;
  return {
    ...rest,
    standsLog: [],
    completedAt: undefined,
    blockedAt: undefined,
    blockerRefs: undefined,
    heldAt: null,
    holdReason: undefined,
  };
}
